import { MappedModal, MappedModalProps } from '@mapped/rivet/dist/mapped/modal'
import { Button, MenuItem, Select } from '@mapped/rivet/dist/mui/material'
import { styled } from '@mapped/rivet/dist/mui/styles'
import { Org } from '@mapped/schema-graph-react-apollo'
import axios from 'axios'
import { FunctionComponent, useContext, useState } from 'react'
import { useQuery } from 'react-query'
import { switchToOrg } from '../../auth/switchOrg'
import { UserContext } from '../../contexts/user'
import { useNotifications } from '../../hooks/useNotifications'
import { Services } from '../../services'
import { logger } from '../../utils/logger'
import { withOrgPath } from '../../url/orgPath'

export const SwitchOrgModal: FunctionComponent<ISwitchOrgModalProps> = ({
  onClose,
  initialOrgId,
}) => {
  const notifications = useNotifications()
  const { organization, authToken } = useContext(UserContext)
  const [isLoading, setIsLoading] = useState(false)
  const [selectedOrg, setSelectedOrg] = useState(
    initialOrgId || organization?.id
  )
  const orgs = useOrgs()

  async function switchOrg() {
    setIsLoading(true)

    const result = await switchToOrg(authToken!, selectedOrg!)

    if (!result.ok) {
      setIsLoading(false)

      return notifications.push({
        type: 'error',
        message: 'Whoops! Something went wrong...',
      })
    }

    // Land on the new org's default Explore view — never the page the switch
    // was made from. Its URL names the OLD org's places, selection and filters,
    // none of which exist in the new one (a building id there is a 404), and
    // its `/orgs/<old>` would make `OrgPathGuard` switch straight back. A
    // LINK's switch (`OrgPathGuard`) is the opposite case: that URL is meant
    // for the org it names, so it is kept.
    window.location.assign(withOrgPath('/explore', selectedOrg))
  }

  return (
    <MappedModal onClose={onClose} open={true} style={{ width: 430 }}>
      <Container>
        <Title>Switch Org</Title>

        <Select
          color="primary"
          variant="outlined"
          value={selectedOrg}
          onChange={(e) => setSelectedOrg(e.target.value as any)}
          style={{ width: '100%' }}
        >
          {orgs.map((org: any) => (
            <MenuItem key={org.id} value={org.id}>
              {org.name}
              {org.id === Services.sandbox.org_id && ' (Sandbox)'}
            </MenuItem>
          ))}
        </Select>

        <Buttons>
          <Button variant="outlined" onClick={onClose} disabled={!!isLoading}>
            Nevermind
          </Button>

          <div style={{ padding: '0 12px' }} />

          <Button
            type="submit"
            loading={!!isLoading}
            disabled={selectedOrg === organization?.id}
            onClick={switchOrg}
          >
            Switch
          </Button>
        </Buttons>
      </Container>
    </MappedModal>
  )
}

/**
 * The orgs the reader belongs to, WITH the query's state — which `useOrgs`
 * flattens away (loading, failed and "no orgs" all read as `[]`). A caller
 * deciding access, `OrgPathGuard`, must not mistake "still loading" for "not a
 * member".
 */
export function useOrgsQuery({ enabled = true }: { enabled?: boolean } = {}) {
  const { authToken } = useContext(UserContext)

  const orgsQuery = useQuery(
    'orgs',

    () =>
      axios
        .get('/api/orgs', { headers: { Authorization: authToken } })
        .then((res) => res?.data),

    {
      enabled,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      // The settled value is the response BODY (`{ orgs }` or `{ error }`),
      // or undefined when the request rejected — so no destructuring here.
      onSettled: (data: any) =>
        data?.error &&
        logger
          .with()
          .any('data', data?.stack)
          .logger()
          .error('Failed to load organizations'),
    }
  )

  const orgs: Org[] = orgsQuery?.data?.orgs || []
  const failed = orgsQuery.isError || !!orgsQuery.data?.error
  // `isIdle` counts as loading: a query that has not started has not said
  // anything about membership either.
  return {
    orgs,
    isLoading: orgsQuery.isLoading || orgsQuery.isIdle,
    failed,
  }
}

export function useOrgs() {
  return useOrgsQuery().orgs
}

const Container = styled.div`
  padding: 36px;
  padding-bottom: 28px;
`

const Title = styled.h2`
  font-size: 32px;
  font-weight: 500;
  line-height: 32px;
  margin-bottom: 48px;
  margin-top: 0;
`

const Buttons = styled.div`
  display: flex;
  width: 100%;
  margin-top: 48px;

  button {
    flex: 1;
  }
`

export interface ISwitchOrgModalProps extends MappedModalProps {
  initialOrgId?: string
}
